import type { BoatPresence } from './boatPresence';

export type BoatEnforcementState = 'paused' | 'follow_up' | 'ok';

export interface BoatEnforcementNotice {
  tone: 'danger' | 'warning';
  title: string;
  detail: string;
}

export interface BoatNameParts {
  lead: string;
  suffix: string;
}

/**
 * Split a trigger label for middle truncation. The final word/number survives,
 * because boat names that differ only by a suffix must remain distinguishable.
 */
export function splitBoatName(name: string): BoatNameParts {
  const finalWord = /^(.*)(\s+\S+)$/.exec(name);
  if (finalWord?.[1] && finalWord[2]) {
    return { lead: finalWord[1], suffix: finalWord[2] };
  }
  if (name.length > 12) {
    return { lead: name.slice(0, -6), suffix: name.slice(-6) };
  }
  return { lead: name, suffix: '' };
}

/** Plain-language support for the shared presence state. */
export function boatConnectionDetail(presence: BoatPresence): string {
  switch (presence) {
    case 'live':
      return 'Receiving data now';
    case 'online':
      return 'Contacted recently';
    case 'connecting':
      return 'Waiting for first contact';
    case 'offline':
      return 'No recent cloud connection';
  }
}

/** The heartbeat value is last-reported state, never an inferred running version. */
export function boatCoreVersionLabel(version: string | null): string {
  return version === null ? 'Version unknown' : `v${version}`;
}

/** Keep connection state and mandatory-update enforcement as separate facts. */
export function boatEnforcementNotice(state: BoatEnforcementState): BoatEnforcementNotice | null {
  switch (state) {
    case 'paused':
      return {
        tone: 'danger',
        title: 'Cloud sync paused',
        detail:
          'A mandatory Core update is required. This update pause does not stop local monitoring on the boat.',
      };
    case 'follow_up':
      return {
        tone: 'warning',
        title: 'Manual update required',
        detail: 'This Core version cannot update remotely. Reinstall it on the boat.',
      };
    case 'ok':
      return null;
  }
}
